import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api.js";
import type { Id } from "../convex-types";

// ==================== MISSION HOOKS ====================

export function useMissions() {
  return useQuery(api.tasks.getMissions);
}

export function useCreateMission() {
  return useMutation(api.tasks.createMission);
}

export function useArchiveMission() {
  return useMutation(api.tasks.archiveMission);
}

// ==================== AGENT HOOKS ====================

export function useAgents() {
  return useQuery(api.tasks.getAgents);
}

export function useCreateAgent() {
  return useMutation(api.tasks.createAgent);
}

export function useUpdateAgentStatus() {
  return useMutation(api.tasks.updateAgentStatus);
}

// ==================== TASK HOOKS ====================

export function useTasks(missionId?: Id<"missions">) {
  return useQuery(api.tasks.getTasks, { missionId });
}

export function useTasksByStatus(status: "inbox" | "assigned" | "blocked" | "in_progress" | "review" | "done", missionId?: Id<"missions">) {
  return useQuery(api.tasks.getTasksByStatus, { status, missionId });
}

export function useTask(taskId: Id<"tasks">) {
  return useQuery(api.tasks.getTask, { taskId });
}

export function useCreateTask() {
  return useMutation(api.tasks.createTask);
}

export function useMoveTask() {
  return useMutation(api.tasks.moveTask);
}

export function useUpdateTask() {
  return useMutation(api.tasks.updateTask);
}

export function useDeleteTask() {
  return useMutation(api.tasks.deleteTask);
}

// ==================== ACTIVITY HOOKS ====================

export function useActivities(missionId?: Id<"missions">) {
  return useQuery(api.tasks.getActivities, { missionId });
}

export function useUnreadActivities(missionId?: Id<"missions">) {
  return useQuery(api.tasks.getUnreadActivities, { missionId });
}

export function useCreateActivity() {
  return useMutation(api.tasks.createActivity);
}

export function useMarkActivityRead() {
  return useMutation(api.tasks.markActivityRead);
}

// ==================== DOCUMENT HOOKS ====================

export function useDocuments(missionId?: Id<"missions">) {
  return useQuery(api.tasks.getDocuments, { missionId });
}

export function useDocumentsByType(type: "deliverable" | "research" | "protocol" | "note", missionId?: Id<"missions">) {
  return useQuery(api.tasks.getDocumentsByType, { type, missionId });
}

export function useCreateDocument() {
  return useMutation(api.tasks.createDocument);
}

// ==================== NOTIFICATION HOOKS ====================

export function useUndeliveredNotifications() {
  return useQuery(api.tasks.getUndeliveredNotifications);
}

export function useCreateNotification() {
  return useMutation(api.tasks.createNotification);
}

export function useMarkNotificationDelivered() {
  return useMutation(api.tasks.markNotificationDelivered);
}

// ==================== OPS LOOP HOOKS ====================

export function useOpsProposals() {
  return useQuery(api.ops.getPendingProposals);
}

export function useOpsApprovals() {
  return useQuery(api.ops.getPendingApprovals);
}

export function useCreateApprovalRequest() {
  return useMutation(api.ops.createApprovalRequest);
}

export function useApproveApprovalRequest() {
  return useMutation(api.ops.approveApprovalRequest);
}

export function useRejectApprovalRequest() {
  return useMutation(api.ops.rejectApprovalRequest);
}

export function useOpsMissions() {
  return useQuery(api.ops.getActiveMissions);
}

export function useOpsSteps(missionId?: Id<"ops_missions">) {
  return useQuery(api.ops.getMissionSteps, { missionId });
}

export function useOpsEvents() {
  return useQuery(api.ops.getOpsEvents);
}

export function useOpsActionRuns() {
  return useQuery(api.ops.getActionRuns);
}

// Combined hooks for OpsPanel (tasks + ops)
export function useCombinedProposals() {
  const opsProposals = useQuery(api.ops.getPendingProposals) || [];
  // Add tasks that could be proposals
  return opsProposals;
}

export function useCombinedMissions() {
  const opsMissions = useQuery(api.ops.getActiveMissions) || [];
  const tasks = useQuery(api.tasks.getTasks, { missionId: "jx73p9d77yzzxmdaqtgee262s580h4zb" as any }) || [];
  return [...opsMissions, ...tasks];
}

export function useCreateProposal() {
  return useMutation(api.ops.createProposal);
}

export function useApproveProposal() {
  return useMutation(api.ops.approveProposal);
}

export function useRejectProposal() {
  return useMutation(api.ops.rejectProposal);
}

export function useEnqueueSteps() {
  return useMutation(api.ops.enqueueSteps);
}

export function useUpdateStepStatus() {
  return useMutation(api.ops.updateStepStatus);
}

export function useLogOpsEvent() {
  return useMutation(api.ops.logEvent);
}
